import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '👋 Hi! I\'m the OwnStack SaaS Savings Assistant.\n\nI can help you:\n✓ Calculate your potential savings\n✓ See if your project fits our 6-month model\n✓ Answer questions about our process\n\nWhat would you like to explore?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat/message`, {
        messages: messages.concat(userMessage).map(m => ({
          role: m.role,
          content: m.content
        })),
        session_id: sessionId,
        user_email: userEmail || null
      });

      // Store session ID from response
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }

      const assistantMessage = {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date(),
        requiresEmail: response.data.requires_email,
        showBooking: response.data.show_booking
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Show email input if requested
      if (response.data.requires_email && !userEmail) {
        setShowEmailInput(true);
      }

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I\'m having trouble connecting. Please try again or contact us directly at hello@ownstack.software',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!userEmail || !userEmail.includes('@')) return;

    setShowEmailInput(false);

    // Save lead
    try {
      await axios.post(`${BACKEND_URL}/api/chat/save-lead`, {
        email: userEmail,
        conversation: messages.map(m => ({
          role: m.role,
          content: m.content
        }))
      });
    } catch (error) {
      console.error('Failed to save lead:', error);
    }

    // Continue conversation
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `Thanks! I've sent a summary to ${userEmail}. How else can I help you today?`,
      timestamp: new Date()
    }]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (showEmailInput) {
        handleEmailSubmit();
      } else {
        handleSend();
      }
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-br from-[#7AC943] to-[#1F6FB2] hover:from-[#6AB833] hover:to-[#0E3A5F] text-white rounded-full p-4 shadow-2xl transition-all duration-300 transform hover:scale-110 flex items-center gap-3 group"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="hidden group-hover:inline-block text-sm font-semibold whitespace-nowrap pr-2">
            Chat with Us
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl flex flex-col border-2 border-gray-200 animate-in slide-in-from-bottom-4 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1F6FB2] to-[#7AC943] text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="8" y="8" width="20" height="6" rx="1.5" fill="#FFFFFF"/>
                  <rect x="6" y="16" width="24" height="6" rx="1.5" fill="#FFFFFF" opacity="0.8"/>
                  <rect x="4" y="24" width="28" height="6" rx="1.5" fill="#FFFFFF" opacity="0.6"/>
                </svg>
              </div>
              <div>
                <div className="font-bold text-sm">SaaS Savings Assistant</div>
                <div className="text-xs text-white/80">Typically replies instantly</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-[#1F6FB2] text-white'
                      : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  <div className={`text-sm leading-relaxed prose prose-sm max-w-none ${
                    message.role === 'user' ? 'prose-invert' : ''
                  }`}>
                    <ReactMarkdown
                      components={{
                        p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                        strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                        ul: ({children}) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                        li: ({children}) => <li className="ml-2">{children}</li>,
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                  {message.showBooking && (
                    <a
                      href="#contact"
                      onClick={() => {
                        setIsOpen(false);
                        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="inline-block mt-3 bg-[#7AC943] hover:bg-[#6AB833] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    >
                      Book Free Consultation
                    </a>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3">
                  <Loader2 className="h-5 w-5 text-[#1F6FB2] animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
            {showEmailInput ? (
              <div className="space-y-2">
                <Input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="your@email.com"
                  className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg"
                />
                <Button
                  onClick={handleEmailSubmit}
                  disabled={!userEmail.includes('@')}
                  className="w-full bg-[#7AC943] hover:bg-[#6AB833] text-white"
                >
                  Submit Email
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  disabled={isLoading}
                  className="flex-1 border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg"
                />
                <Button
                  onClick={handleSend}
                  disabled={isLoading || !input.trim()}
                  className="bg-[#1F6FB2] hover:bg-[#0E3A5F] text-white px-4"
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
